import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getAllArticles } from '../../services/firebase/blogService';
import { FiSearch, FiCalendar, FiClock, FiChevronRight, FiLayout, FiChevronLeft, FiChevronDown } from 'react-icons/fi';
import { Helmet } from 'react-helmet-async';
import Footer from '../../components/Footer'; // Footer bileşenini import edin

const BlogListing = () => {
  const [articles, setArticles] = useState([]);
  const [filteredArticles, setFilteredArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid'); // 'grid' veya 'list'
  
  // Sayfalama için eklenecek state'ler
  const [currentPage, setCurrentPage] = useState(1);
  const [articlesPerPage, setArticlesPerPage] = useState(9); // Grid görünümünde her sayfada 9 makale
  const [paginatedArticles, setPaginatedArticles] = useState([]);

  useEffect(() => {
    const fetchArticles = async () => {
      try {
        setLoading(true);
        const fetchedArticles = await getAllArticles();
        // Sadece yayınlanmış makaleleri filtrele ve tarihe göre sırala
        const publishedArticles = fetchedArticles
          .filter(article => article.isPublished)
          .sort((a, b) => {
            // createdAt timestamp'ini Date nesnesine dönüştür
            const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date();
            const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date();
            return dateB - dateA; // Yeniden eskiye doğru sırala
          });
        
        setArticles(publishedArticles);
        setFilteredArticles(publishedArticles);
      } catch (error) {
        console.error('Makaleler yüklenirken hata:', error);
        setError('Makaleler yüklenirken bir hata oluştu.');
      } finally {
        setLoading(false);
      }
    };

    fetchArticles();
  }, []);

  useEffect(() => {
    // Arama teriminin veya kategorinin değişimini izle
    const applyFilters = () => {
      let result = articles;
      
      // Arama terimiyle filtrele
      if (searchTerm) {
        result = result.filter(article => {
          const searchLower = searchTerm.toLowerCase();
          return (
            article.title.toLowerCase().includes(searchLower) ||
            article.description.toLowerCase().includes(searchLower) ||
            (article.keywords && article.keywords.toLowerCase().includes(searchLower))
          );
        });
      }
      
      // Kategoriyle filtrele
      if (activeCategory !== 'all') {
        result = result.filter(article => article.categoryId === activeCategory);
      }
      
      setFilteredArticles(result);
      setCurrentPage(1); // Filtre değiştiğinde ilk sayfaya dön
    };
    
    applyFilters();
  }, [searchTerm, activeCategory, articles]);

  // Görünüm moduna bağlı olarak sayfa başına düşen makale sayısını ayarla
  useEffect(() => {
    setArticlesPerPage(viewMode === 'grid' ? 9 : 5);
    setCurrentPage(1); // Görünüm değiştiğinde ilk sayfaya dön
  }, [viewMode]);

  // Sayfalama işlemi için makaleleri bölme
  useEffect(() => {
    const indexOfLastArticle = currentPage * articlesPerPage;
    const indexOfFirstArticle = indexOfLastArticle - articlesPerPage;
    const current = filteredArticles.slice(indexOfFirstArticle, indexOfLastArticle);
    setPaginatedArticles(current);
  }, [filteredArticles, currentPage, articlesPerPage]);

  // Sayfa değiştirme fonksiyonu
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Toplam sayfa sayısını hesapla
  const totalPages = Math.ceil(filteredArticles.length / articlesPerPage);

  // Kategorileri makalelerden çıkar
  const categories = ['all', ...new Set(articles.map(article => article.categoryId).filter(Boolean))];
  
  // Tarihi formatla
  const formatDate = (timestamp) => {
    if (!timestamp) return 'Tarih yok';
    
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('tr-TR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Okuma süresini hesapla (ortalama 200 kelime/dakika)
  const calculateReadingTime = (content) => {
    if (!content) return '1 dk';
    const wordCount = content.trim().split(/\s+/).length;
    const readingTime = Math.ceil(wordCount / 200);
    return `${readingTime} dk`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-900 flex flex-col items-center justify-center text-white p-4">
        <h1 className="text-2xl font-bold mb-4">Hata</h1>
        <p className="text-gray-300">{error}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 pt-16 pb-0"> {/* pb-24 yerine pb-0 kullanın */}
      <Helmet>
        <title>Finans & Yatırım Eğitimleri | Smart Portfoy</title>
        <meta 
          name="description" 
          content="Finansal özgürlüğünüze giden yolda ihtiyacınız olan tüm bilgiler, stratejiler ve pratik rehberler. Yatırım, borsa ve finans eğitimleri." 
        />
        <meta 
          name="keywords" 
          content="finans eğitimi, yatırım, borsa, finansal okuryazarlık, para yönetimi, yatırım stratejileri" 
        />
        <meta property="og:title" content="Finans & Yatırım Eğitimleri | Smart Portfoy" />
        <meta 
          property="og:description" 
          content="Finansal özgürlüğünüze giden yolda yatırım ve finans eğitimleri." 
        />
        <meta property="og:type" content="website" />
        <meta property="og:url" content={window.location.href} />
        <meta property="og:image" content="/splogo_192_p.png" />
        <link rel="canonical" href={window.location.href} />
      </Helmet>
      
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-900 to-purple-900 py-16">
        <div className="container mx-auto px-4 max-w-6xl">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">Finans & Yatırım Eğitimleri</h1>
          <p className="text-xl text-blue-100 max-w-3xl">
            Finansal özgürlüğünüze giden yolda ihtiyacınız olan tüm bilgiler, stratejiler ve pratik rehberler.
          </p>
        </div>
      </header>

      {/* Filtre ve arama bölümü */}
      <div className="container mx-auto px-4 max-w-6xl py-8">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
          {/* Arama kutusu */}
          <div className="relative w-full md:w-auto md:min-w-[300px]">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FiSearch className="text-gray-500" />
            </div>
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Konularda ara..."
              className="block w-full pl-10 pr-4 py-2 rounded-lg bg-gray-800 border-gray-700 text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          
          {/* Kategori filtreleri */}
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => setActiveCategory(category)}
                className={`px-4 py-2 text-sm rounded-full transition ${
                  activeCategory === category
                    ? 'bg-blue-600 text-white'
                    : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                }`}
              >
                {category === 'all' ? 'Tümü' : category}
              </button>
            ))}
          </div>
          
          {/* Görünüm değiştirme */}
          <div className="flex border border-gray-700 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 ${viewMode === 'grid' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'}`}
            >
              <FiLayout className="w-5 h-5" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`p-2 ${viewMode === 'list' ? 'bg-blue-600 text-white' : 'bg-gray-800 text-gray-400'}`}
            >
              <FiLayout className="w-5 h-5 rotate-90" />
            </button>
          </div>
        </div>

        {/* Sonuç sayısı */}
        <p className="text-gray-400 mb-6">
          {filteredArticles.length} eğitim bulundu
          {searchTerm && (
            <span> - "{searchTerm}" için arama sonuçları</span>
          )}
        </p>

        {/* Makaleler listesi */}
        {filteredArticles.length === 0 ? (
          <div className="text-center py-16">
            <h3 className="text-xl font-medium text-gray-300 mb-2">Hiç makale bulunamadı.</h3>
            <p className="text-gray-400">Lütfen farklı bir arama terimi deneyin veya tüm makaleleri görüntüleyin.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {paginatedArticles.map((article) => (
              <div
                key={article.id}
                className="bg-gray-800 rounded-lg overflow-hidden shadow-lg transition-transform hover:transform hover:scale-105"
              >
                {article.imageUrl && (
                  <div className="h-48 overflow-hidden">
                    <img
                      src={article.imageUrl}
                      alt={article.title}
                      className="w-full h-full object-cover transition-transform hover:scale-110"
                    />
                  </div>
                )}
                <div className="p-5">
                  <div className="flex items-center text-xs text-gray-400 mb-3">
                    <FiCalendar className="mr-1" /> {formatDate(article.createdAt)}
                    <span className="mx-2">•</span>
                    <FiClock className="mr-1" /> {calculateReadingTime(article.content)}
                  </div>
                  <h2 className="text-xl font-semibold text-white mb-2 line-clamp-2">
                    {article.title}
                  </h2>
                  <p className="text-gray-300 mb-4 line-clamp-3">
                    {article.description}
                  </p>
                  <Link
                    to={`/egitim/${article.slug}`}
                    className="inline-flex items-center text-blue-400 hover:text-blue-300"
                  >
                    Daha fazla oku <FiChevronRight className="ml-1" />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="space-y-6">
            {paginatedArticles.map((article) => (
              <div
                key={article.id}
                className="bg-gray-800 rounded-lg overflow-hidden shadow-lg hover:bg-gray-750 transition-all"
              >
                <div className="flex flex-col md:flex-row">
                  {article.imageUrl && (
                    <div className="md:w-1/4 h-48 md:h-auto">
                      <img
                        src={article.imageUrl}
                        alt={article.title}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  )}
                  <div className="p-6 md:w-3/4">
                    <div className="flex items-center text-xs text-gray-400 mb-3">
                      <FiCalendar className="mr-1" /> {formatDate(article.createdAt)}
                      <span className="mx-2">•</span>
                      <FiClock className="mr-1" /> {calculateReadingTime(article.content)}
                    </div>
                    <h2 className="text-2xl font-semibold text-white mb-3">
                      {article.title}
                    </h2>
                    <p className="text-gray-300 mb-4">
                      {article.description}
                    </p>
                    <Link
                      to={`/egitim/${article.slug}`}
                      className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
                    >
                      Daha fazla oku <FiChevronRight className="ml-1" />
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Sayfalama Kontrolü */}
        {totalPages > 1 && (
          <div className="flex justify-center items-center space-x-2 mt-12 mb-8">
            <button
              onClick={() => paginate(Math.max(currentPage - 1, 1))}
              disabled={currentPage === 1}
              className={`px-4 py-2 rounded-md ${
                currentPage === 1 
                  ? 'bg-gray-700 text-gray-500' 
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              <FiChevronLeft />
            </button>
            
            <div className="flex space-x-1">
              {/* İlk sayfa */}
              {currentPage > 2 && (
                <button
                  onClick={() => paginate(1)}
                  className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700"
                >
                  1
                </button>
              )}
              
              {/* Ellipsis */}
              {currentPage > 3 && (
                <span className="px-4 py-2 text-gray-500">
                  <FiChevronDown className="transform rotate-90" />
                </span>
              )}
              
              {/* Önceki sayfa */}
              {currentPage > 1 && (
                <button
                  onClick={() => paginate(currentPage - 1)}
                  className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700"
                >
                  {currentPage - 1}
                </button>
              )}
              
              {/* Mevcut sayfa */}
              <button
                className="px-4 py-2 rounded-md bg-blue-600 text-white"
              >
                {currentPage}
              </button>
              
              {/* Sonraki sayfa */}
              {currentPage < totalPages && (
                <button
                  onClick={() => paginate(currentPage + 1)}
                  className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700"
                >
                  {currentPage + 1}
                </button>
              )}
              
              {/* Ellipsis */}
              {currentPage < totalPages - 2 && (
                <span className="px-4 py-2 text-gray-500">
                  <FiChevronDown className="transform rotate-90" />
                </span>
              )}
              
              {/* Son sayfa */}
              {currentPage < totalPages - 1 && (
                <button
                  onClick={() => paginate(totalPages)}
                  className="px-4 py-2 rounded-md bg-gray-800 text-white hover:bg-gray-700"
                >
                  {totalPages}
                </button>
              )}
            </div>
            
            <button
              onClick={() => paginate(Math.min(currentPage + 1, totalPages))}
              disabled={currentPage === totalPages}
              className={`px-4 py-2 rounded-md ${
                currentPage === totalPages 
                  ? 'bg-gray-700 text-gray-500' 
                  : 'bg-gray-800 text-white hover:bg-gray-700'
              }`}
            >
              <FiChevronRight />
            </button>
          </div>
        )}
        
        {/* Her sayfa için gösterilen makale sayısı seçimi */}
        <div className="flex justify-center items-center mb-12">
          <select
            value={articlesPerPage}
            onChange={(e) => {
              setArticlesPerPage(Number(e.target.value));
              setCurrentPage(1);
            }}
            className="bg-gray-800 text-white px-3 py-2 rounded-md border border-gray-700"
          >
            <option value={viewMode === 'grid' ? 6 : 3}>Daha az göster</option>
            <option value={viewMode === 'grid' ? 9 : 5}>Orta</option>
            <option value={viewMode === 'grid' ? 12 : 8}>Daha fazla göster</option>
          </select>
        </div>
        
        {/* Footer bileşenini ekleyin */}
        <Footer />
      </div>
    </div>
  );
};

export default BlogListing;